/**
 * 掲示板スレッド（board_posts/{postId}/thread_messages）の hooks。
 * 仕様書: docs/02_work/done/plan_board_feature.md §2-3, §5-3
 *
 * - 読込は Firestore 直接購読（onSnapshot）。家訓: コールバック内は setState のみ。
 * - 書込はクライアント直書き（firestore.rules で保護）。safeWrite 経由（サーキットブレーカー）。
 * - リアクションは arrayUnion/arrayRemove で { uid, emoji } の完全一致オブジェクトを操作する。
 *   これにより並行更新に強く、かつ更新対象が reactions フィールドのみに限定されるため
 *   rules の hasOnly(['reactions']) を通過する。
 * - threadSeen の更新は markBoardThreadSeen Callable（スレッドの開閉時に呼ぶ）。
 */
import type { BoardThreadMessage } from '@goenhub/shared';
import { BOARD_COLLECTIONS } from '@goenhub/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { db, functions } from '@/lib/firebase';
import { safeAddDoc, safeDeleteDoc, safeUpdateDoc } from '@/lib/safeWrite';

export interface ThreadMessageRecord extends BoardThreadMessage {
  id: string;
}

const markBoardThreadSeenFn = httpsCallable<{ postId: string }, { updated: boolean }>(
  functions,
  'markBoardThreadSeen',
);

/** thread_messages のコレクション参照。 */
function threadCol(postId: string) {
  return collection(db, BOARD_COLLECTIONS.posts, postId, BOARD_COLLECTIONS.threadMessages);
}

/**
 * スレッドメッセージを時系列（createdAt 昇順）で購読する。
 * enabled=false（パネル未オープン / postId 未確定）のときは購読しない。
 * 家訓: onSnapshot コールバック内は setState のみ。
 */
export function useThreadMessages(
  postId: string | null | undefined,
  enabled: boolean,
): { messages: ThreadMessageRecord[]; loading: boolean } {
  const [messages, setMessages] = useState<ThreadMessageRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!postId || !enabled) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(threadCol(postId), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setMessages(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as BoardThreadMessage) })),
        );
        setLoading(false);
      },
      (err) => {
        console.warn('[useThreadMessages] snapshot error:', err);
        setMessages([]);
        setLoading(false);
      },
    );
    return unsub;
  }, [postId, enabled]);

  return { messages, loading };
}

/**
 * スレッドメッセージの操作（送信・編集・削除・リアクション・既読）をまとめて返す。
 * uid / authorName は useAuth から解決する。
 */
export function useThreadActions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const uid = user?.uid ?? null;
  const authorName = user?.displayName ?? user?.email ?? '';

  /** メッセージ送信（クライアント直書き create）。 */
  const sendThreadMessage = useCallback(
    async (
      postId: string,
      input: { body: string; imageUrls: string[] },
    ): Promise<void> => {
      if (!uid) throw new Error('未ログインのため送信できません');
      await safeAddDoc(threadCol(postId), {
        authorUid: uid,
        authorName,
        body: input.body,
        imageUrls: input.imageUrls,
        reactions: [],
        createdAt: serverTimestamp(),
        editedAt: null,
      });
    },
    [uid, authorName],
  );

  /** 本文編集（本人のみ。rules で authorUid 一致を検証）。 */
  const editThreadMessage = useCallback(
    async (postId: string, messageId: string, body: string): Promise<void> => {
      await safeUpdateDoc(
        doc(db, BOARD_COLLECTIONS.posts, postId, BOARD_COLLECTIONS.threadMessages, messageId),
        { body, editedAt: serverTimestamp() },
      );
    },
    [],
  );

  /** メッセージ削除（本人のみ）。 */
  const deleteThreadMessage = useCallback(
    async (postId: string, messageId: string): Promise<void> => {
      await safeDeleteDoc(
        doc(db, BOARD_COLLECTIONS.posts, postId, BOARD_COLLECTIONS.threadMessages, messageId),
      );
    },
    [],
  );

  /**
   * リアクションのトグル。
   * mine=true（既に自分が付けている）→ arrayRemove、false → arrayUnion。
   * { uid, emoji } の完全一致オブジェクトを操作し、更新を reactions フィールドのみに限定する。
   */
  const toggleReaction = useCallback(
    async (
      postId: string,
      messageId: string,
      emoji: string,
      mine: boolean,
    ): Promise<void> => {
      if (!uid) throw new Error('未ログインのためリアクションできません');
      const value = { uid, emoji };
      await safeUpdateDoc(
        doc(db, BOARD_COLLECTIONS.posts, postId, BOARD_COLLECTIONS.threadMessages, messageId),
        { reactions: mine ? arrayRemove(value) : arrayUnion(value) },
      );
    },
    [uid],
  );

  /**
   * スレッド既読化（threadSeen[me] = threadCount）。
   * パネルの開閉時に呼ぶ。成功したら一覧バッジを消すため ['board_posts'] を無効化する。
   */
  const markThreadSeen = useCallback(
    async (postId: string): Promise<void> => {
      try {
        await markBoardThreadSeenFn({ postId });
        void queryClient.invalidateQueries({ queryKey: ['board_posts'] });
      } catch (err) {
        console.warn('[useThreadActions] markThreadSeen failed:', err);
      }
    },
    [queryClient],
  );

  return {
    sendThreadMessage,
    editThreadMessage,
    deleteThreadMessage,
    toggleReaction,
    markThreadSeen,
  };
}
